import React, { useEffect, useState, useRef } from 'react';
import { X, FileText, Send, Users, Newspaper, Upload, Film, Copy, UserCheck, Globe } from 'lucide-react';
import { collection, query, where, getDocs, getDoc, doc, addDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import type { ReportHistoryItem, Client } from '../types';
import toast from 'react-hot-toast';
import { cities, neighborhoods } from '../data';
import AdMediaViewer from './AdMediaViewer';
import AdPreviewModal, { AdGeneratingModal } from './AdPreviewModal';
import type { AdDraft } from './AdPreviewModal';
import { generateLandingUrl } from '../utils/landingUrl';
import { normalizeCityName, normalizeCityArray } from '../utils/cityNormalization';
import NeighborhoodInsights from './NeighborhoodInsights';
import PropertyScoreModal from './PropertyScoreModal';
import SharePropertyWizard from './SharePropertyWizard';
import { getMyNetworkPosts } from '../utils/networkApi';
import type { NetworkPost } from '../utils/networkApi';

const MAKE_REPORT_REGENERATE_ADV_WEBHOOK_URL = import.meta.env.VITE_MAKE_REPORT_REGENERATE_ADV_WEBHOOK_URL;
if (!MAKE_REPORT_REGENERATE_ADV_WEBHOOK_URL) {
  console.warn('Missing VITE_MAKE_REPORT_REGENERATE_ADV_WEBHOOK_URL');
}

interface ReportHistoryModalProps {
  userId: string;
  onClose: () => void;
  onAdCreated?: () => Promise<void>;
  onUpdateAdsCount?: () => Promise<boolean>;
  onLoadData?: (formId: string) => void;
  onLeadClick?: (leadId: string) => void;
}

export default function ReportHistoryModal({ userId, onClose, onAdCreated, onUpdateAdsCount, onLoadData, onLeadClick }: ReportHistoryModalProps) {
  const [reports, setReports] = useState<ReportHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sendingReport, setSendingReport] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userPhone, setUserPhone] = useState<string>('');
  const [userBranch, setUserBranch] = useState<string>('');
  const [showMatchClientsModal, setShowMatchClientsModal] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [matchingClients, setMatchingClients] = useState<Client[]>([]);
  const [selectedPropertyData, setSelectedPropertyData] = useState<any>(null);
  const [alreadyMatchedClientIds, setAlreadyMatchedClientIds] = useState<string[]>([]);
  const [showAdMediaViewer, setShowAdMediaViewer] = useState<string | null>(null);
  const [showMatchSellerModal, setShowMatchSellerModal] = useState<string | null>(null);
  const [sellers, setSellers] = useState<Client[]>([]);
  const [assigningSeller, setAssigningSeller] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState<string | null>(null);
  const [currentPropertySeller, setCurrentPropertySeller] = useState<string | null>(null);
  const [assignedPropertySellers, setAssignedPropertySellers] = useState<Map<string, string>>(new Map());
  const [sellerAgentNames, setSellerAgentNames] = useState<Map<string, string>>(new Map());
  const [sellerNames, setSellerNames] = useState<Map<string, string>>(new Map());
  const [loadingPropertySellers, setLoadingPropertySellers] = useState(true);
  const [showAdTypeModal, setShowAdTypeModal] = useState(false);
  const [selectedAdType, setSelectedAdType] = useState<'buyer' | 'seller' | null>(null);
  const [selectedAdVariant, setSelectedAdVariant] = useState<string | null>(null);
  const [pendingFormId, setPendingFormId] = useState<string | null>(null);
  const [adDraft, setAdDraft] = useState<AdDraft | null>(null);
  const [showAdPreview, setShowAdPreview] = useState(false);
  const [shareNetworkFormId, setShareNetworkFormId] = useState<string | null>(null);
  const [networkPostMap, setNetworkPostMap] = useState<Map<string, NetworkPost>>(new Map());
  const [networkPostsLoading, setNetworkPostsLoading] = useState(false);
  const [isGeneratingAd, setIsGeneratingAd] = useState(false);
  const [isRegeneratingAd, setIsRegeneratingAd] = useState(false);
  const [isSendingAd, setIsSendingAd] = useState(false);
  const [pendingPropertyAddress, setPendingPropertyAddress] = useState('');
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const lastAdGenerationTime = useRef<number>(0);
  const lastAdType = useRef<'buyer' | 'seller' | null>(null);
  const lastAdVariant = useRef<string | null>(null);

  const [isOfficeView, setIsOfficeView] = useState(false);
  const [filterAgentId, setFilterAgentId] = useState<string>('all');
  const [filterCity, setFilterCity] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [filterSeller, setFilterSeller] = useState<'all' | 'assigned' | 'unassigned'>('all');

  console.log('🎯 ReportHistoryModal rendered with userId:', userId);
  console.log('🔥 CODE VERSION: 2.0 - WITH USEEFFECT FIX');

  const markAsCompleted = async (formId: string) => {
    if (!window.confirm('לסמן נכס זה כהושלם?')) return;
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('שגיאה בעדכון הסטטוס');
        return;
      }
      const idToken = await currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/update-property-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formId, status: 'completed' }),
      });
      if (!response.ok) {
        toast.error('שגיאה בעדכון הסטטוס');
        return;
      }
      const result = await response.json();
      if (!result.success) {
        toast.error('שגיאה בעדכון הסטטוס');
        return;
      }
      setReports(prev => prev.map(r => r.formId === formId ? { ...r, status: 'completed' } : r));
      toast.success('הסטטוס עודכן להושלם');
    } catch (e) {
      console.error('Failed to update status:', e);
      toast.error('שגיאה בעדכון הסטטוס');
    }
  };

  // Fetch property sellers function - standalone so we can call it anytime
  const fetchPropertySellers = async () => {
    try {
      console.log('🚀 fetchPropertySellers called');
      setLoadingPropertySellers(true);

      if (!auth.currentUser) {
        console.warn('⚠️ No currentUser, cannot fetch property sellers');
        setLoadingPropertySellers(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken(true); // Force refresh token
      if (!idToken) {
        console.warn('⚠️ No idToken, cannot fetch property sellers');
        setLoadingPropertySellers(false);
        return;
      }

      console.log('🔑 Got idToken, length:', idToken.length, 'first 20 chars:', idToken.substring(0, 20));

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/get-property-sellers`;
      console.log('📡 Fetching property sellers from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not OK:', errorText);
        console.error('❌ Full response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
        setLoadingPropertySellers(false);
        return;
      }

      const result = await response.json();
      const data = result.data || [];

      console.log('🔵 Property sellers fetched:', data.length, 'items');
      console.log('🔵 Raw data:', data);

      const sellersMap = new Map<string, string>();
      const agentNamesMap = new Map<string, string>();
      const sellerNamesMap = new Map<string, string>();
      data.forEach((item: any) => {
        console.log('📌 Adding to map:', item.form_id, '->', item.seller_id);
        sellersMap.set(item.form_id, item.seller_id);
        if (item.agentName) agentNamesMap.set(item.form_id, item.agentName);
        if (item.sellerName) sellerNamesMap.set(item.form_id, item.sellerName);
      });

      console.log('🗺️ Final sellersMap size:', sellersMap.size);
      console.log('🗺️ Map entries:', Array.from(sellersMap.entries()));
      setAssignedPropertySellers(sellersMap);
      setSellerAgentNames(agentNamesMap);
      setSellerNames(sellerNamesMap);
      console.log('✅ Property sellers state updated');
    } catch (error) {
      console.error('❌ Error fetching property sellers:', error);
    } finally {
      setLoadingPropertySellers(false);
    }
  };

  useEffect(() => {
    console.log('🎯 useEffect triggered with userId:', userId);

    const fetchReports = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserEmail(userData.email || '');
          setUserName(userData.fullName || '');
          setUserPhone(userData.phone || '');
          setUserBranch(userData.branchName || '');
        }

        // Resolve office context via get-office edge function
        let ownerOrManager = false;
        let memberUids: string[] = [];
        const agentNameMap: Record<string, string> = {};

        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const idToken = await currentUser.getIdToken();
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const officeRes = await fetch(`${supabaseUrl}/functions/v1/get-office`, {
              headers: { 'Authorization': `Bearer ${idToken}` },
            });
            if (officeRes.ok) {
              const officeData = await officeRes.json();
              if (officeData.success && officeData.office) {
                const myRole = officeData.office.myRole as string;
                if (myRole === 'owner' || myRole === 'manager') {
                  ownerOrManager = true;
                  memberUids = (officeData.office.members as any[]).map((m) => m.firebase_uid as string);
                  for (const m of officeData.office.members as any[]) {
                    agentNameMap[m.firebase_uid] = m.display_name || m.email || m.firebase_uid;
                  }
                }
              }
            }
          } catch (officeErr) {
            console.error('Failed to fetch office context for reports:', officeErr);
          }
        }

        let reportsList: ReportHistoryItem[];

        if (ownerOrManager && memberUids.length > 0) {
          // Batch into chunks of 30 (Firestore 'in' query limit)
          const chunks: string[][] = [];
          for (let i = 0; i < memberUids.length; i += 30) {
            chunks.push(memberUids.slice(i, i + 30));
          }
          const snapshots = await Promise.all(
            chunks.map((chunk) =>
              getDocs(query(collection(db, 'forms'), where('userId', 'in', chunk)))
            )
          );
          reportsList = snapshots
            .flatMap((snap) => snap.docs)
            .map((d) => {
              const data = d.data();
              const agentUid = (data.userId as string) || null;
              return {
                formId: d.id,
                reportId: data.reportId,
                generatedAt: data.generatedAt,
                lastModified: data.lastModified,
                version: data.version || 1,
                status: data.status || 'completed',
                createdAt: data.createdAt || data.timestamp || new Date().toISOString(),
                address: {
                  city: data.address?.city || '',
                  neighborhood: data.address?.neighborhood || '',
                  street: data.address?.street || '',
                  houseNumber: data.address?.houseNumber || '',
                },
                agentId: agentUid,
                agentName: agentUid ? (agentNameMap[agentUid] || null) : null,
              } as ReportHistoryItem;
            })
            .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());
          setIsOfficeView(true);
        } else {
          const q = query(
            collection(db, 'forms'),
            where('userId', '==', userId)
          );
          const querySnapshot = await getDocs(q);
          reportsList = querySnapshot.docs
            .map((d) => {
              const data = d.data();
              return {
                formId: d.id,
                reportId: data.reportId,
                generatedAt: data.generatedAt,
                lastModified: data.lastModified,
                version: data.version || 1,
                status: data.status || 'completed',
                createdAt: data.createdAt || data.timestamp || new Date().toISOString(),
                address: {
                  city: data.address?.city || '',
                  neighborhood: data.address?.neighborhood || '',
                  street: data.address?.street || '',
                  houseNumber: data.address?.houseNumber || '',
                },
                agentId: null,
                agentName: null,
              } as ReportHistoryItem;
            })
            .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());
          setIsOfficeView(false);
        }

        setReports(reportsList);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setIsLoading(false);
      }
    };

    console.log('🚀 useEffect: About to call fetchReports and fetchPropertySellers');
    fetchReports();
    fetchPropertySellers();
    fetchNetworkPosts();
  }, [userId]);

  // Load existing Network posts to detect duplicates and show status.
  // Uses the deployed edge function — never reads network_property_posts directly.
  const fetchNetworkPosts = async () => {
    setNetworkPostsLoading(true);
    try {
      const posts = await getMyNetworkPosts();
      const map = new Map<string, NetworkPost>();
      for (const p of posts) {
        if (p.status !== 'removed') {
          map.set(p.source_property_id, p);
        }
      }
      setNetworkPostMap(map);
    } catch {
      // Silently fail — network status is non-critical UI
      console.warn('Failed to load network posts');
    } finally {
      setNetworkPostsLoading(false);
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) {
          console.error('No auth token available');
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const apiUrl = `${supabaseUrl}/functions/v1/get-leads`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch leads');
        }

        const data = await response.json();
        const allLeads = data.leads || [];
        setClients(allLeads);
        setSellers(allLeads.filter((lead: Client) => lead.leadType === 'seller'));
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [userId]);

  const handleResendReport = async (formId: string) => {
    setPendingFormId(formId);
    setShowAdTypeModal(true);
  };

  const generateAdCopy = async (formData?: any) => {
    if (!selectedAdType || !selectedAdVariant || !pendingFormId) return;

    const now = Date.now();
    if (now - lastAdGenerationTime.current < 5000) {
      toast.error('נא להמתין מספר שניות בין הפקות');
      return;
    }
    lastAdGenerationTime.current = now;

    const data = formData || pendingFormData;
    if (!data) return;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error('יש להתחבר מחדש כדי ליצור מודעה');
      throw new Error('not_authenticated');
    }
    const idToken = await currentUser.getIdToken();

    const response = await fetch(`${supabaseUrl}/functions/v1/generate-property-ad-copy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        property_id: pendingFormId,
        audience: selectedAdType,
        message_type: selectedAdVariant,
        property: {
          city: data.address?.city || data.city || '',
          street: data.address?.street || data.street || '',
          neighborhood: data.address?.neighborhood || data.neighborhood || '',
          propertyType: data.property?.propertyType || data.propertyType || '',
          rooms: data.property?.rooms || data.rooms,
          size: data.property?.size || data.size,
          price: data.property?.price || data.price,
          floor: data.property?.floor || data.floor,
          elevator: data.property?.hasElevator || data.elevator,
          parking: data.property?.parkingSpots > 0 || data.parking,
          condition: data.property?.condition || data.condition || '',
          description: data.property?.description || data.description || '',
          imageUrl: data.propertyImages?.[0] || '',
        },
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Edge function error:', response.status, errorBody);
      if (response.status === 429) {
        throw new Error('rate_limit');
      }
      throw new Error('generation_failed');
    }

    return await response.json();
  };

  const handleSendWithAdData = async () => {
    if (!selectedAdType || !selectedAdVariant) {
      toast.error('יש לבחור סוג קהל וסוג מודעה');
      return;
    }

    if (!pendingFormId) return;

    try {
      setIsGeneratingAd(true);
      setShowAdTypeModal(false);

      if (onUpdateAdsCount) {
        const canProceed = await onUpdateAdsCount();
        if (!canProceed) {
          setIsGeneratingAd(false);
          setPendingFormId(null);
          setSelectedAdType(null);
          setSelectedAdVariant(null);
          return;
        }
      }

      const formDoc = await getDoc(doc(db, 'forms', pendingFormId));
      if (!formDoc.exists()) {
        throw new Error('Report not found');
      }

      const formData = formDoc.data();
      setPendingFormData(formData);

      const address = [formData.address?.street, formData.address?.city].filter(Boolean).join(', ');
      setPendingPropertyAddress(address || 'נכס');

      lastAdType.current = selectedAdType;
      lastAdVariant.current = selectedAdVariant;

      const adResult = await generateAdCopy(formData);

      setAdDraft(adResult);
      setShowAdPreview(true);
    } catch (error: any) {
      console.error('Error generating ad:', error);
      if (error.message === 'rate_limit') {
        toast.error('המערכת עמוסה, נסה שוב בעוד כמה שניות');
      } else {
        toast.error('שגיאה ביצירת המודעה');
      }
      setSelectedAdType(null);
      setSelectedAdVariant(null);
    } finally {
      setIsGeneratingAd(false);
    }
  };

  const handleRegenerateAd = async () => {
    if (!pendingFormId || !pendingFormData) {
      toast.error('חסרים נתונים ליצירה מחדש');
      return;
    }

    const adType = lastAdType.current || selectedAdType;
    const adVariant = lastAdVariant.current || selectedAdVariant;

    if (!adType || !adVariant) {
      toast.error('חסר סוג מודעה ליצירה מחדש');
      return;
    }

    try {
      setIsRegeneratingAd(true);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const data = pendingFormData;

      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('יש להתחבר מחדש כדי ליצור מודעה');
        return;
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-property-ad-copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          property_id: pendingFormId,
          audience: adType,
          message_type: adVariant,
          property: {
            city: data.address?.city || data.city || '',
            street: data.address?.street || data.street || '',
            neighborhood: data.address?.neighborhood || data.neighborhood || '',
            propertyType: data.property?.propertyType || data.propertyType || '',
            rooms: data.property?.rooms || data.rooms,
            size: data.property?.size || data.size,
            price: data.property?.price || data.price,
            floor: data.property?.floor || data.floor,
            elevator: data.property?.hasElevator || data.elevator,
            parking: data.property?.parkingSpots > 0 || data.parking,
            condition: data.property?.condition || data.condition || '',
            description: data.property?.description || data.description || '',
            imageUrl: data.propertyImages?.[0] || '',
          },
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('Edge function error on regenerate:', response.status, errorBody);
        if (response.status === 429) {
          throw new Error('rate_limit');
        }
        throw new Error('generation_failed');
      }

      const adResult = await response.json();
      setAdDraft(adResult);
    } catch (error: any) {
      if (error.message === 'rate_limit') {
        toast.error('המערכת עמוסה, נסה שוב בעוד כמה שניות');
      } else {
        toast.error('שגיאה ביצירת המודעה');
      }
    } finally {
      setIsRegeneratingAd(false);
    }
  };

  const handleSendAdToMake = async (adData: AdDraft) => {
    if (!pendingFormId || !pendingFormData) return;

    try {
      setIsSendingAd(true);
      const landingPageUrl = generateLandingUrl(userId, pendingFormId);

      const adGeneratedAt = new Date().toISOString();

      const response = await fetch(MAKE_REPORT_REGENERATE_ADV_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: pendingFormId,
          formId: pendingFormId,
          userId,
          userEmail,
          userName,
          userPhone,
          userBranch,
          landingPageUrl,
          ad_type: selectedAdType,
          ad_variant: selectedAdVariant,
          headline: adData.headline,
          subheadline: adData.subheadline,
          body: adData.body,
          cta: adData.cta,
          adGeneratedAt,
          ...pendingFormData,
        }),
      });

      if (!response.ok) throw new Error('Failed to send ad');

      if (onAdCreated) await onAdCreated();

      toast.success('המודעה נשלחה בהצלחה');
      setShowAdPreview(false);
      setAdDraft(null);
      setPendingFormId(null);
      setPendingFormData(null);
      setSelectedAdType(null);
      setSelectedAdVariant(null);
    } catch (error) {
      console.error('Error sending ad to Make:', error);
      toast.error('שגיאה בשליחת המודעה');
    } finally {
      setIsSendingAd(false);
    }
  };

  const normalizeNeighborhoodName = (neighborhoodName: string | undefined, cityValue: string | undefined): string | undefined => {
    if (!neighborhoodName || !cityValue) return undefined;

    const cityNeighborhoods = neighborhoods[cityValue] || [];
    const neighborhoodObj = cityNeighborhoods.find(n => n.label === neighborhoodName || n.value === neighborhoodName);
    return neighborhoodObj ? neighborhoodObj.value : neighborhoodName;
  };

  const getMatchingClients = (propertyData: any): Client[] => {
    const PRICE_TOLERANCE = 0.05;

    const propertyCity = normalizeCityName(propertyData.address?.city);
    const propertyNeighborhood = normalizeNeighborhoodName(propertyData.address?.neighborhood, propertyCity);

    console.log('=== Property Data ===', {
      price: propertyData.property?.price,
      rooms: propertyData.property?.rooms,
      cityOriginal: propertyData.address?.city,
      cityNormalized: propertyCity,
      neighborhoodOriginal: propertyData.address?.neighborhood,
      neighborhoodNormalized: propertyNeighborhood
    });

    return clients.filter(client => {
      const criteria = client.searchCriteria;
      const propertyPrice = propertyData.property?.price || 0;

      console.log(`--- Checking client: ${client.name} ---`);
      console.log('Client criteria:', criteria);

      if (criteria.priceMin && propertyPrice < criteria.priceMin * (1 - PRICE_TOLERANCE)) {
        console.log(`❌ Price too low: ${propertyPrice} < ${criteria.priceMin * (1 - PRICE_TOLERANCE)}`);
        return false;
      }

      if (criteria.priceMax && propertyPrice > criteria.priceMax * (1 + PRICE_TOLERANCE)) {
        console.log(`❌ Price too high: ${propertyPrice} > ${criteria.priceMax * (1 + PRICE_TOLERANCE)}`);
        return false;
      }

      if (criteria.rooms && criteria.rooms.length > 0) {
        const propertyRooms = propertyData.property?.rooms;
        if (!criteria.rooms.includes(propertyRooms)) {
          console.log(`❌ Rooms mismatch: ${propertyRooms} not in [${criteria.rooms}]`);
          return false;
        }
      }

      if (criteria.cities && criteria.cities.length > 0) {
        const normalizedCriteriaCities = normalizeCityArray(criteria.cities);
        if (!normalizedCriteriaCities.includes(propertyCity)) {
          console.log(`❌ City mismatch: "${propertyCity}" not in [${normalizedCriteriaCities}]`);
          return false;
        }
      }

      if (criteria.neighborhoods && criteria.neighborhoods.length > 0) {
        if (!criteria.neighborhoods.includes(propertyNeighborhood)) {
          console.log(`❌ Neighborhood mismatch: "${propertyNeighborhood}" not in [${criteria.neighborhoods}]`);
          return false;
        }
      }

      console.log(`✅ Client ${client.name} matches!`);
      return true;
    });
  };

  const handleAssignSeller = async (formId: string, sellerId: string) => {
    try {
      setAssigningSeller(true);

      console.log('Assigning seller:', { formId, sellerId });

      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        toast.error('שגיאה באימות');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/assign-property-seller`;

      console.log('Calling API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId,
          sellerId
        }),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        const errorMessage = errorData.details || errorData.error || 'Failed to assign seller';
        toast.error(`שגיאה: ${errorMessage}`);
        return;
      }

      const result = await response.json();
      console.log('Success result:', result);

      toast.success('הנכס הותאם למוכר בהצלחה');
      setShowMatchSellerModal(null);
      setCurrentPropertySeller(sellerId);

      // Immediately update the local map
      setAssignedPropertySellers(prev => {
        console.log('🔄 Updating assignedPropertySellers Map locally');
        const newMap = new Map(prev);
        newMap.set(formId, sellerId);
        console.log('✅ Added to Map:', formId, '->', sellerId);
        return newMap;
      });

      // Also refresh from server to ensure consistency
      console.log('🔄 Refreshing property sellers from server...');
      await fetchPropertySellers();
    } catch (error: any) {
      console.error('Error assigning seller:', error);
      toast.error(`שגיאה בהקצאת מוכר: ${error.message || 'Unknown error'}`);
    } finally {
      setAssigningSeller(false);
    }
  };

  const handleMatchClients = async (formId: string) => {
    try {
      if (selectedClients.length === 0) {
        toast.error('אנא בחר לקוח אחד לפחות');
        return;
      }

      const formDoc = await getDoc(doc(db, 'forms', formId));
      if (!formDoc.exists()) {
        throw new Error('Property not found');
      }

      const propertyData = formDoc.data();

      for (const clientId of selectedClients) {
        await addDoc(collection(db, 'property_client_matches'), {
          formId: formId,
          clientId,
          userId,
          status: 'new',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      toast.success(`הנכס הותאם ל-${selectedClients.length} לקוחות`);
      setShowMatchClientsModal(null);
      setSelectedClients([]);
    } catch (error) {
      console.error('Error matching clients:', error);
      toast.error('שגיאה בהתאמת לקוחות');
    }
  };

  // Stage 1: apply agent + city + date (seller counts are derived from this)
  const baseFilteredReports: ReportHistoryItem[] = (() => {
    let result = isOfficeView && filterAgentId !== 'all'
      ? reports.filter((r) => r.agentId === filterAgentId)
      : reports;

    if (filterCity !== 'all') {
      result = result.filter((r) => r.address.city === filterCity);
    }

    if (filterDate !== 'all') {
      const now = new Date();
      let cutoff: string | null = null;
      let cutoffEnd: string | null = null;
      if (filterDate === 'today') {
        cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      } else if (filterDate === '7days') {
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (filterDate === '30days') {
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      } else if (filterDate === 'custom') {
        if (customDateFrom) cutoff = new Date(customDateFrom + 'T00:00:00').toISOString();
        if (customDateTo) cutoffEnd = new Date(customDateTo + 'T23:59:59').toISOString();
      }
      if (cutoff) result = result.filter((r) => r.createdAt != null && r.createdAt >= cutoff!);
      if (cutoffEnd) result = result.filter((r) => r.createdAt != null && r.createdAt <= cutoffEnd!);
    }

    return result;
  })();

  // Stage 2: seller counts from base (ignores seller filter so counts stay stable)
  const sellerAssignedCount = baseFilteredReports.filter((r) => assignedPropertySellers.has(r.formId)).length;
  const sellerUnassignedCount = baseFilteredReports.length - sellerAssignedCount;

  // Stage 3: apply seller assignment filter on top
  const filteredReports: ReportHistoryItem[] =
    filterSeller === 'all'
      ? baseFilteredReports
      : baseFilteredReports.filter((r) =>
          filterSeller === 'assigned'
            ? assignedPropertySellers.has(r.formId)
            : !assignedPropertySellers.has(r.formId)
        );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2" dir="rtl">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="sticky top-0 bg-gray-800 p-2 border-b border-gray-700 flex justify-between items-center rounded-t-lg">
          <h2 className="text-base font-bold flex items-center gap-1">
            <FileText className="text-blue-400 w-4 h-4" />
            Property Actions
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                console.log('🔄 Manual refresh triggered');
                fetchPropertySellers();
              }}
              className="text-gray-400 hover:text-blue-400 transition-colors"
              title="רענן שיוכים"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        
        <div className="p-2 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400 mx-auto"></div>
              <p className="mt-1 text-sm text-gray-400">טוען היסטוריה...</p>
            </div>
          ) : reports.length > 0 ? (
            <div className="space-y-2">
              {(() => {
                console.log('📊 Current assignedPropertySellers Map size:', assignedPropertySellers.size);
                console.log('📊 Map contents:', Array.from(assignedPropertySellers.entries()));
                console.log('📊 loadingPropertySellers:', loadingPropertySellers);
                return null;
              })()}
              {/* Agent filter — visible to office owner/manager only */}
              {isOfficeView && (() => {
                const seen = new Set<string>();
                const agents: { id: string; name: string }[] = [];
                for (const r of reports) {
                  if (r.agentId && !seen.has(r.agentId)) {
                    seen.add(r.agentId);
                    agents.push({ id: r.agentId, name: r.agentName || r.agentId });
                  }
                }
                if (agents.length < 2) return null;
                return (
                  <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-750 rounded-lg border border-gray-600">
                    <span className="text-xs text-gray-400 font-medium">סוכן:</span>
                    <button
                      onClick={() => setFilterAgentId('all')}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterAgentId === 'all' ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    >
                      כל הסוכנים
                    </button>
                    {agents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => setFilterAgentId(agent.id)}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterAgentId === agent.id ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                      >
                        {agent.name}
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* City filter — shown when 2+ distinct cities exist */}
              {(() => {
                const seen = new Set<string>();
                const citiesInReports: string[] = [];
                for (const r of reports) {
                  const city = r.address.city?.trim();
                  if (city && !seen.has(city)) {
                    seen.add(city);
                    citiesInReports.push(city);
                  }
                }
                if (citiesInReports.length < 2) return null;
                citiesInReports.sort((a, b) => a.localeCompare(b, 'he'));
                return (
                  <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-750 rounded-lg border border-gray-600">
                    <span className="text-xs text-gray-400 font-medium">עיר:</span>
                    <button
                      onClick={() => setFilterCity('all')}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterCity === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    >
                      הכל
                    </button>
                    {citiesInReports.map((city) => (
                      <button
                        key={city}
                        onClick={() => setFilterCity(city)}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterCity === city ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* Date filter — always visible */}
              <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-750 rounded-lg border border-gray-600">
                <span className="text-xs text-gray-400 font-medium">תאריך יצירה:</span>
                {(['all', 'today', '7days', '30days', 'custom'] as const).map((opt) => {
                  const labels: Record<string, string> = {
                    all: 'הכל', today: 'היום', '7days': '7 ימים', '30days': '30 ימים', custom: 'טווח מותאם',
                  };
                  return (
                    <button
                      key={opt}
                      onClick={() => { setFilterDate(opt); if (opt !== 'custom') { setCustomDateFrom(''); setCustomDateTo(''); } }}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterDate === opt ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    >
                      {labels[opt]}
                    </button>
                  );
                })}
                {filterDate === 'custom' && (
                  <div className="flex items-center gap-1.5 mt-1 w-full">
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-0.5 text-xs text-white focus:outline-none focus:border-blue-500"
                      placeholder="מתאריך"
                    />
                    <span className="text-xs text-gray-400">—</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-0.5 text-xs text-white focus:outline-none focus:border-blue-500"
                      placeholder="עד תאריך"
                    />
                  </div>
                )}
              </div>

              {/* Seller assignment filter */}
              <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-gray-750 rounded-lg border border-gray-600">
                <span className="text-xs text-gray-400 font-medium">שיוך מוכר:</span>
                {(['all', 'assigned', 'unassigned'] as const).map((opt) => {
                  const label =
                    opt === 'all' ? 'הכל' :
                    opt === 'assigned' ? `משויך למוכר${!loadingPropertySellers ? ` (${sellerAssignedCount})` : ''}` :
                    `ללא מוכר${!loadingPropertySellers ? ` (${sellerUnassignedCount})` : ''}`;
                  return (
                    <button
                      key={opt}
                      onClick={() => setFilterSeller(opt)}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${filterSeller === opt ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {filteredReports.map((report) => {
                const isAssigned = assignedPropertySellers.has(report.formId);
                console.log(`🔍 Rendering report ${report.formId}: isAssigned=${isAssigned}, loadingPropertySellers=${loadingPropertySellers}`);
                return (
                <div
                  key={report.formId}
                  className="bg-gray-700 p-3 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <div className="flex gap-4">
                    {/* Property details */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white leading-snug">
                        {[report.address.city, report.address.street, report.address.houseNumber]
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {report.status === 'draft' ? (
                          <button
                            type="button"
                            onClick={() => markAsCompleted(report.formId)}
                            title="לחץ לסימון כהושלם"
                            className="text-xs px-2 py-0.5 rounded font-medium bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/40 hover:text-yellow-100 transition-colors cursor-pointer"
                          >
                            טיוטה
                          </button>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded font-medium bg-green-500/20 text-green-300">
                            הושלם
                          </span>
                        )}
                        {report.agentName && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-900/60 text-teal-300 border border-teal-700/40">
                            {report.agentName}
                          </span>
                        )}
                        {isAssigned && sellerNames.get(report.formId) && (
                          <button
                            onClick={() => {
                              const sellerId = assignedPropertySellers.get(report.formId);
                              if (sellerId && onLeadClick) {
                                onLeadClick(sellerId);
                                onClose();
                              } else {
                                toast.error('לא נמצא כרטיס מוכר ב-CRM');
                              }
                            }}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/60 text-amber-300 border border-amber-700/40 hover:bg-amber-800/60 hover:border-amber-600/60 transition-colors cursor-pointer"
                          >
                            מוכר: {sellerNames.get(report.formId)}
                          </button>
                        )}
                      </div>
                      <NeighborhoodInsights
                        city={report.address.city}
                        neighborhood={report.address.neighborhood || ''}
                      />
                      <div className="flex items-center gap-1.5 mt-2">
                        <button
                          onClick={() => setShowScoreModal(report.formId)}
                          className="text-xs text-yellow-400 hover:text-yellow-300 underline underline-offset-2 transition-colors"
                        >
                          דרג נכס →
                        </button>
                      </div>
                      <div className="text-xs text-gray-400 mt-1.5">
                        {report.reportId ? (
                          <>
                            מזהה דוח: {report.reportId}
                            {report.version > 1 && (
                              <span className="mr-2 text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">
                                גרסה {report.version}
                              </span>
                            )}
                          </>
                        ) : (
                          `מזהה פנימי: ${report.formId}`
                        )}
                      </div>
                      {report.generatedAt && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {new Date(report.generatedAt).toLocaleDateString('he-IL')} {new Date(report.generatedAt).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                          {report.lastModified && report.version > 1 && (
                            <span className="mr-2">
                              • עודכן: {new Date(report.lastModified).toLocaleDateString('he-IL')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0 self-center">
                      <button
                        onClick={() => {
                          const propertyUrl = generateLandingUrl(userId, report.formId, 'buyer');
                          navigator.clipboard.writeText(propertyUrl);
                          toast.success('קישור לקונה הועתק בהצלחה');
                        }}
                        className="p-2 bg-teal-600 hover:bg-teal-700 rounded transition-colors"
                        title="העתק קישור לקונה"
                      >
                        <Copy size={15} className="text-white" />
                      </button>
                      <button
                        onClick={() => {
                          const propertyUrl = generateLandingUrl(userId, report.formId, 'seller');
                          navigator.clipboard.writeText(propertyUrl);
                          toast.success('קישור למוכר הועתק בהצלחה');
                        }}
                        className="p-2 bg-orange-600 hover:bg-orange-700 rounded transition-colors"
                        title="העתק קישור למוכר"
                      >
                        <Copy size={15} className="text-white" />
                      </button>
                      <button
                        onClick={() => {
                          setShowAdMediaViewer(report.formId);
                          setSelectedPropertyData(report.address);
                        }}
                        className="p-2 bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                        title="צפה במודעות שנוצרו"
                      >
                        <Film size={15} className="text-white" />
                      </button>
                      <button
                        onClick={() => {
                          if (onLoadData) {
                            onLoadData(report.formId);
                            onClose();
                          }
                        }}
                        className="p-2 bg-indigo-600 hover:bg-indigo-700 rounded transition-colors"
                        title="טען נתוני נכס"
                      >
                        <Upload size={15} className="text-white" />
                      </button>
                      <button
                        onClick={() => {
                          if (loadingPropertySellers) {
                            toast.error('טוען נתונים...');
                            return;
                          }
                          if (isAssigned) {
                            toast.error('נכס זה כבר מותאם למוכר');
                            return;
                          }
                          setShowMatchSellerModal(report.formId);
                        }}
                        disabled={isAssigned || loadingPropertySellers}
                        className={`p-2 rounded transition-colors ${
                          loadingPropertySellers
                            ? 'bg-gray-600 cursor-wait'
                            : isAssigned
                            ? 'bg-amber-600 cursor-not-allowed'
                            : 'bg-amber-600 hover:bg-amber-700'
                        }`}
                        style={isAssigned ? { opacity: 0.4 } : {}}
                        title={loadingPropertySellers ? 'טוען נתונים...' : isAssigned ? 'נכס זה כבר מותאם למוכר' : 'התאם מוכר לנכס'}
                      >
                        {loadingPropertySellers ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        ) : (
                          <UserCheck size={15} className="text-white" />
                        )}
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const formDoc = await getDoc(doc(db, 'forms', report.formId));
                          if (formDoc.exists()) {
                            const propertyData = formDoc.data();
                            setSelectedPropertyData(propertyData);
                            const matching = getMatchingClients(propertyData);
                            setMatchingClients(matching);

                            const matchesQuery = query(
                              collection(db, 'property_client_matches'),
                              where('formId', '==', report.formId),
                              where('userId', '==', userId)
                            );
                            const matchesSnapshot = await getDocs(matchesQuery);
                            const alreadyMatched = matchesSnapshot.docs.map(doc => doc.data().clientId);
                            setAlreadyMatchedClientIds(alreadyMatched);

                            setShowMatchClientsModal(report.formId);
                          }
                        }}
                        className="p-2 bg-green-600 hover:bg-green-700 rounded transition-colors"
                        title="התאם לקוחות לנכס"
                      >
                        <Users size={15} className="text-white" />
                      </button>
                      <button
                        onClick={() => handleResendReport(report.formId)}
                        disabled={sendingReport === report.formId}
                        className="p-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                        title="הפק מודעת פרסום"
                      >
                        <Newspaper size={15} className={`text-white ${sendingReport === report.formId ? 'animate-pulse' : ''}`} />
                      </button>
                      {report.status === 'completed' && (() => {
                        const existingPost = networkPostMap.get(report.formId);
                        if (existingPost) {
                          const statusLabels: Record<string, string> = {
                            draft: 'טיוטה', pending_approval: 'ממתין', active: 'פעיל',
                            paused: 'מושהה', matched: 'מותאם', closed: 'סגור', expired: 'פג תוקף',
                          };
                          const statusColors: Record<string, string> = {
                            draft: 'bg-gray-600', pending_approval: 'bg-yellow-600',
                            active: 'bg-green-600', paused: 'bg-orange-600',
                            matched: 'bg-blue-600', closed: 'bg-gray-700',
                            expired: 'bg-red-600',
                          };
                          return (
                            <a
                              href="/network"
                              onClick={(e) => { e.preventDefault(); window.location.href = '/network'; }}
                              className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${statusColors[existingPost.status] ?? 'bg-gray-600'} text-white`}
                              title={`נכס זה כבר ברשת — סטטוס: ${statusLabels[existingPost.status] ?? existingPost.status}`}
                            >
                              <Globe size={12} />
                              {statusLabels[existingPost.status] ?? existingPost.status}
                            </a>
                          );
                        }
                        return (
                          <button
                            onClick={() => setShareNetworkFormId(report.formId)}
                            className="p-2 bg-teal-600 hover:bg-teal-700 rounded transition-colors"
                            title="שתף ב-RV Network"
                          >
                            <Globe size={15} className="text-white" />
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400">
              <FileText size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">לא נמצאו דוחות בהיסטוריה</p>
            </div>
          )}
        </div>
      </div>

      {showMatchClientsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]" dir="rtl">
          <div className="bg-gray-800 rounded-lg max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Users className="text-green-400" size={20} />
                התאם לקוחות לנכס
              </h3>
              <button
                onClick={() => {
                  setShowMatchClientsModal(null);
                  setSelectedClients([]);
                  setAlreadyMatchedClientIds([]);
                  setMatchingClients([]);
                  setSelectedPropertyData(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {selectedPropertyData && (
                <div className="mb-4 p-3 bg-gray-700 rounded-lg border border-blue-500">
                  <div className="text-sm font-semibold text-blue-400 mb-2">פרטי הנכס:</div>
                  <div className="text-sm text-gray-300 space-y-1">
                    <div>📍 {selectedPropertyData.address?.city} - {selectedPropertyData.address?.neighborhood}</div>
                    <div>💰 מחיר: ₪{selectedPropertyData.property?.price?.toLocaleString()}</div>
                    <div>🏠 {selectedPropertyData.property?.rooms} חדרים</div>
                  </div>
                </div>
              )}

              {matchingClients.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>אין לקוחות מתאימים לנכס זה</p>
                  <p className="text-sm mt-2">הקריטריונים של הלקוחות לא תואמים את הנכס</p>
                  {clients.length === 0 && (
                    <p className="text-sm mt-2">הוסף לקוחות דרך מערכת CRM</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-3 text-sm text-green-400 font-semibold">
                    נמצאו {matchingClients.length} לקוחות מתאימים:
                  </div>
                  <div className="space-y-2">
                    {matchingClients.map(client => {
                      const propertyPrice = selectedPropertyData?.property?.price || 0;
                      const isOverBudget = client.searchCriteria.priceMax && propertyPrice > client.searchCriteria.priceMax;
                      const isAlreadyMatched = alreadyMatchedClientIds.includes(client.id);

                      return (
                        <label
                          key={client.id}
                          className={`flex items-center gap-3 p-3 bg-gray-700 rounded-lg transition-colors border ${
                            isAlreadyMatched
                              ? 'opacity-50 cursor-not-allowed border-gray-600'
                              : 'hover:bg-gray-600 cursor-pointer border-green-500/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedClients.includes(client.id)}
                            disabled={isAlreadyMatched}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedClients(prev => [...prev, client.id]);
                              } else {
                                setSelectedClients(prev => prev.filter(id => id !== client.id));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-600 disabled:cursor-not-allowed"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-white">{client.name}</div>
                              {isAlreadyMatched && (
                                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">
                                  כבר מקושר
                                </span>
                              )}
                              {!isAlreadyMatched && isOverBudget && (
                                <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded">
                                  מעל תקציב
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-400">{client.phone}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {client.searchCriteria.priceMin && client.searchCriteria.priceMax ? (
                                <span>₪{client.searchCriteria.priceMin.toLocaleString()}-{client.searchCriteria.priceMax.toLocaleString()}</span>
                              ) : client.searchCriteria.priceMin ? (
                                <span>מ-₪{client.searchCriteria.priceMin.toLocaleString()}</span>
                              ) : client.searchCriteria.priceMax ? (
                                <span>עד ₪{client.searchCriteria.priceMax.toLocaleString()}</span>
                              ) : null}
                              {client.searchCriteria.rooms && client.searchCriteria.rooms.length > 0 && (
                                <span className="mr-2">• {client.searchCriteria.rooms.join(', ')} חדרים</span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-700 flex gap-2">
              <button
                onClick={() => handleMatchClients(showMatchClientsModal)}
                disabled={selectedClients.length === 0}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                התאם {selectedClients.length > 0 && `(${selectedClients.length})`}
              </button>
              <button
                onClick={() => {
                  setShowMatchClientsModal(null);
                  setSelectedClients([]);
                  setAlreadyMatchedClientIds([]);
                  setMatchingClients([]);
                  setSelectedPropertyData(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {showMatchSellerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]" dir="rtl">
          <div className="bg-gray-800 rounded-lg max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <UserCheck className="text-amber-400" size={20} />
                התאם מוכר לנכס
              </h3>
              <button
                onClick={() => {
                  setShowMatchSellerModal(null);
                  setCurrentPropertySeller(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {sellers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <UserCheck size={48} className="mx-auto mb-4 opacity-50" />
                  <p>אין מוכרי נכסים במערכת</p>
                  <p className="text-sm mt-2">הוסף מוכרים דרך מערכת CRM</p>
                </div>
              ) : (
                <>
                  <div className="mb-3 text-sm text-amber-400 font-semibold">
                    בחר מוכר לנכס זה:
                  </div>
                  <div className="space-y-2">
                    {sellers.map(seller => (
                      <button
                        key={seller.id}
                        onClick={() => handleAssignSeller(showMatchSellerModal, seller.id)}
                        disabled={assigningSeller}
                        className="w-full text-right flex items-center gap-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors border border-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-white">{seller.name}</div>
                          <div className="text-sm text-gray-400">{seller.phone}</div>
                          {seller.email && (
                            <div className="text-xs text-gray-500 mt-1">{seller.email}</div>
                          )}
                        </div>
                        {assigningSeller && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500"></div>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowMatchSellerModal(null);
                  setCurrentPropertySeller(null);
                }}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdMediaViewer && (
        <AdMediaViewer
          propertyId={showAdMediaViewer}
          onClose={() => {
            setShowAdMediaViewer(null);
            setSelectedPropertyData(null);
          }}
          propertyAddress={selectedPropertyData}
          onLeadClick={(leadId) => {
            setShowAdMediaViewer(null);
            setSelectedPropertyData(null);
            onLeadClick?.(leadId);
          }}
        />
      )}

      {showScoreModal && (() => {
        const report = reports.find(r => r.formId === showScoreModal);
        if (!report) return null;
        return (
          <PropertyScoreModal
            formId={showScoreModal}
            city={report.address.city}
            neighborhood={report.address.neighborhood || ''}
            onClose={() => setShowScoreModal(null)}
          />
        );
      })()}

      {showAdTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[80] flex items-center justify-center p-4" dir="rtl">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">בחר סוג מודעה</h3>
              <button
                onClick={() => {
                  setShowAdTypeModal(false);
                  setSelectedAdType(null);
                  setSelectedAdVariant(null);
                  setPendingFormId(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-3">קהל יעד:</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAdType('buyer');
                      setSelectedAdVariant(null);
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedAdType === 'buyer'
                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                        : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">👤</div>
                      <div className="font-medium">קונה</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAdType('seller');
                      setSelectedAdVariant(null);
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedAdType === 'seller'
                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                        : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">💰</div>
                      <div className="font-medium">מוכר</div>
                    </div>
                  </button>
                </div>
              </div>

              {selectedAdType && (
                <div>
                  <label className="block text-sm font-medium mb-3">סוג מודעה:</label>
                  <div className="grid grid-cols-1 gap-2">
                    {selectedAdType === 'buyer' ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('info')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'info'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">📋 מודעת מידע</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('opportunity')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'opportunity'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">🎯 הזדמנות</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('emotion')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'emotion'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">❤️ רגש / לייף סטייל</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('proof')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'proof'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">✅ הוכחת יכולת</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('valuation')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'valuation'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">💎 הערכת שווי</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedAdVariant('pain')}
                          className={`p-3 rounded-lg border-2 transition-all text-right ${
                            selectedAdVariant === 'pain'
                              ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-medium">🔥 נקודת כאב</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setShowAdTypeModal(false);
                  setSelectedAdType(null);
                  setSelectedAdVariant(null);
                  setPendingFormId(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSendWithAdData}
                disabled={!selectedAdType || !selectedAdVariant || isGeneratingAd}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingAd ? 'יוצר מודעה...' : 'הפק מודעה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isGeneratingAd && (
        <AdGeneratingModal propertyAddress={pendingPropertyAddress} />
      )}

      {showAdPreview && adDraft && (
        <AdPreviewModal
          draft={adDraft}
          propertyAddress={pendingPropertyAddress}
          onClose={() => {
            setShowAdPreview(false);
            setAdDraft(null);
            setPendingFormId(null);
            setPendingFormData(null);
            setSelectedAdType(null);
            setSelectedAdVariant(null);
          }}
          onRegenerate={handleRegenerateAd}
          onSendToMake={handleSendAdToMake}
          isRegenerating={isRegeneratingAd}
          isSending={isSendingAd}
        />
      )}
      {shareNetworkFormId && (() => {
        const report = reports.find(r => r.formId === shareNetworkFormId);
        if (!report) return null;
        return (
          <SharePropertyWizard
            formId={shareNetworkFormId}
            address={report.address}
            onClose={() => { setShareNetworkFormId(null); fetchNetworkPosts(); }}
          />
        );
      })()}
    </div>
  );
}